/**
 * PublicProfile — read-only profile page for any non-suspended angler.
 * Route: /u/:userId
 *
 * Renders the allow-listed public profile data from the backend:
 * banner / avatar / Elite badge, stat strip (total catches, PB, favourite
 * lake, joined), recent catches gallery, and a follow-button placeholder
 * (future social feature). No email/admin data is ever fetched.
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Crown, Trophy, Fish, MapPin, Calendar, UserPlus, UserCheck, Loader2, ArrowLeft,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import CatchImage from '../components/CatchImage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }); }
  catch { return '—'; }
};

const Avatar = ({ profile, size = 'lg' }) => {
  const dim = size === 'lg' ? 'w-20 h-20 text-2xl' : 'w-12 h-12 text-base';
  if (profile?.avatar_url) {
    return <img src={profile.avatar_url} alt={profile.username} className={`${dim} rounded-full object-cover border-2 border-[#c8b273]/40`} onError={(e) => { e.currentTarget.style.display = 'none'; }} />;
  }
  return (
    <div className={`${dim} rounded-full bg-[#141714] border-2 border-[#c8b273]/40 flex items-center justify-center text-[#c8b273] font-bold`}>
      {(profile?.username || '?').charAt(0).toUpperCase()}
    </div>
  );
};

export default function PublicProfile() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('carpcast_token');
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/members/${userId}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!cancelled) setProfile(r.data);
      } catch (err) {
        if (!cancelled) {
          if (err.response?.status === 404) setNotFound(true);
          else toast.error('Could not load profile');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [userId, token]);

  const toggleFollow = async () => {
    if (!token) {
      toast.error('Sign in to follow anglers');
      navigate('/login');
      return;
    }
    if (followBusy || !profile) return;
    setFollowBusy(true);
    const wasFollowing = !!profile.is_following;
    setProfile((p) => ({ ...p, is_following: !wasFollowing }));
    try {
      if (wasFollowing) {
        await axios.delete(`${API}/users/${userId}/follow`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.message(`Unfollowed ${profile.username}`);
      } else {
        await axios.post(`${API}/users/${userId}/follow`, {}, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success(`Following ${profile.username}`);
      }
    } catch (err) {
      setProfile((p) => ({ ...p, is_following: wasFollowing }));
      toast.error(err.response?.data?.detail || 'Could not update follow');
    } finally {
      setFollowBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0b0d0b] flex items-center justify-center px-4 py-12" data-testid="public-profile-loading">
        <Loader2 className="w-6 h-6 text-[#c8b273] animate-spin" />
      </div>
    );
  }

  if (notFound || !profile) {
    return (
      <div className="min-h-screen bg-[#0b0d0b] flex items-center justify-center px-4 py-12" data-testid="public-profile-404">
        <div className="glass-card p-10 max-w-md w-full text-center">
          <Fish className="w-12 h-12 text-[#4a504a] mx-auto mb-4" />
          <h1 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase text-white mb-2">Angler not found</h1>
          <p className="text-[#8a948a] text-sm mb-6">That profile doesn't exist or is unavailable.</p>
          <Button onClick={() => navigate('/members')} className="btn-gold">Back to discover</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0b0d0b] px-4 py-8 md:py-12" data-testid="public-profile">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-[#8a948a] hover:text-white text-sm mb-4"
          data-testid="public-profile-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        {/* Banner / identity card */}
        <div className="glass-card overflow-hidden mb-6" data-testid="public-profile-card">
          {/* Decorative banner — gradient + subtle pattern. */}
          <div className="h-24 bg-gradient-to-br from-[#1a2b1f] via-[#0b0d0b] to-[#1a1f2b]" />

          <div className="px-5 pb-5 -mt-12">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
              <div className="flex items-end gap-4">
                <Avatar profile={profile} size="lg" />
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h1 className="font-['Barlow_Condensed'] text-2xl md:text-3xl font-extrabold uppercase tracking-wider text-white" data-testid="public-profile-username">
                      {profile.username}
                    </h1>
                    {profile.is_elite && (
                      <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded bg-[#c8b273]/15 text-[#c8b273] border border-[#c8b273]/30 font-bold uppercase tracking-wider">
                        <Crown className="w-3 h-3" /> Elite
                      </span>
                    )}
                  </div>
                  {profile.location && (
                    <p className="text-[#8a948a] text-sm flex items-center gap-1 mt-0.5">
                      <MapPin className="w-3 h-3" /> {profile.location}
                    </p>
                  )}
                  {profile.bio && (
                    <p className="text-[#c0c4c0] text-sm mt-2 max-w-md">{profile.bio}</p>
                  )}
                </div>
              </div>

              {/* Follow / Following — functional toggle, hidden for anonymous viewers. */}
              {!profile.is_self && token && (
                <Button
                  variant={profile.is_following ? 'outline' : 'default'}
                  className={profile.is_following
                    ? 'border-[#c8b273]/40 text-[#c8b273] hover:bg-[#c8b273]/10'
                    : 'btn-gold'}
                  onClick={toggleFollow}
                  disabled={followBusy}
                  data-testid="public-profile-follow-btn"
                >
                  {followBusy ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : profile.is_following ? (
                    <UserCheck className="w-4 h-4 mr-2" />
                  ) : (
                    <UserPlus className="w-4 h-4 mr-2" />
                  )}
                  {profile.is_following ? 'Following' : 'Follow'}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Stat strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="public-profile-stats">
          <Stat label="Total catches" value={profile.catch_count ?? 0} icon={<Fish className="w-4 h-4" />} />
          <Stat
            label="Personal best"
            value={profile.pb_weight ? `${profile.pb_weight}lb` : '—'}
            sub={profile.pb_fish_type ? `${profile.pb_fish_type}` : null}
            icon={<Trophy className="w-4 h-4 text-[#c8b273]" />}
          />
          <Stat label="Favourite lake" value={profile.favourite_lake || '—'} icon={<MapPin className="w-4 h-4" />} />
          <Stat label="Joined" value={formatDate(profile.joined_at)} icon={<Calendar className="w-4 h-4" />} />
        </div>

        {/* Recent catches gallery */}
        <div className="glass-card p-5" data-testid="public-profile-recent">
          <h2 className="font-['Barlow_Condensed'] text-lg font-bold uppercase tracking-wider text-white mb-4 flex items-center gap-2">
            <Fish className="w-4 h-4 text-[#c8b273]" /> Recent catches
          </h2>
          {profile.recent_catches?.length ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {profile.recent_catches.map((c) => (
                <div key={c.id} className="bg-[#0b0d0b] border border-[#1f221f] rounded overflow-hidden" data-testid={`public-profile-catch-${c.id}`}>
                  <CatchImage row={c} aspectRatio="square" />
                  <div className="p-2">
                    <p className="text-white text-sm font-bold truncate">{c.fish_type}</p>
                    <p className="text-[#c8b273] text-xs">{c.weight}lb</p>
                    {c.lake_name && <p className="text-[#6b7d6b] text-[11px] truncate">{c.lake_name}</p>}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[#8a948a] text-sm py-6 text-center">No catches shared yet.</p>
          )}
        </div>

        <p className="text-center text-[10px] text-[#4a504a] uppercase tracking-wider mt-6">
          <Link to="/members" className="hover:text-[#c8b273]">← Browse more anglers</Link>
        </p>
      </div>
    </div>
  );
}

function Stat({ label, value, sub, icon }) {
  return (
    <div className="glass-card p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#8a948a]">
        {icon} {label}
      </div>
      <p className="text-white font-bold text-lg mt-1 truncate">{value}</p>
      {sub && <p className="text-[#c8b273] text-xs truncate">{sub}</p>}
    </div>
  );
}
